/**
 * App Navigator
 * Root navigation that switches between Auth, Onboarding, and Main navigators
 */

import React, { useEffect } from 'react';
import { Platform } from 'react-native';
import { createStackNavigator } from '@react-navigation/stack';
import { useAuthStore } from '../store/authStore';
import { useOnboardingStore } from '../store/onboardingStore';
import AuthNavigator from './AuthNavigator';
import MainNavigator from './MainNavigator';
import OnboardingScreen from '../screens/OnboardingScreen';
import MagicAuthScreen from '../screens/MagicAuthScreen';
import GoogleAuthScreen from '../screens/GoogleAuthScreen';
import AuthErrorScreen from '../screens/AuthErrorScreen';
import Loading from '../components/common/Loading';
import { colors } from '../theme/colors';
import logger from '../utils/logger';

const Stack = createStackNavigator();

export default function AppNavigator() {
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuthStore();
  const {
    currentStep,
    isLoading: isOnboardingLoading,
    fetchStatus,
  } = useOnboardingStore();

  logger.log('[AppNavigator] Rendering with state:', {
    isAuthenticated,
    isAuthLoading,
    isOnboardingLoading,
    userId: user?.id,
    onboardingStep: currentStep?.step,
    onboardingCompleted: currentStep?.completed,
  });

  // Replace stale onboarding history entry when onboarding completes on web
  useEffect(() => {
    if (isAuthenticated && currentStep?.step === 'complete') {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, [isAuthenticated, currentStep?.step]);

  // Fetch onboarding status when user is authenticated
  useEffect(() => {
    logger.log('[AppNavigator] useEffect - isAuthenticated:', isAuthenticated, 'currentStep:', currentStep);
    if (isAuthenticated && !currentStep) {
      logger.log('[AppNavigator] Fetching onboarding status');
      fetchStatus();
    } else if (isAuthenticated && currentStep) {
      logger.log('[AppNavigator] Already have currentStep, skipping fetch:', currentStep);
    }
  }, [isAuthenticated, currentStep, fetchStatus]);

  // Show loading while checking authentication or onboarding
  // IMPORTANT: Wait for currentStep to be loaded before showing any screen
  if (isAuthLoading || (isAuthenticated && !currentStep && isOnboardingLoading)) {
    logger.log('[AppNavigator] Showing loading screen');
    return <Loading fullScreen message="Chargement..." />;
  }

  // If authenticated but we still don't have currentStep, keep loading
  if (isAuthenticated && !currentStep && !isOnboardingLoading) {
    logger.log('[AppNavigator] Waiting for onboarding status...');
    return <Loading fullScreen message="Vérification du profil..." />;
  }

  // If authenticated, check onboarding status
  if (isAuthenticated) {
    // If currentStep is null or step is not 'complete', show onboarding
    const needsOnboarding = !currentStep || currentStep.step !== 'complete';
    logger.log('[AppNavigator] Authenticated - needs onboarding:', needsOnboarding, {
      currentStep: currentStep?.step,
      completed: currentStep?.completed,
    });

    return (
      <Stack.Navigator
        screenOptions={{
          headerShown: false,
          cardStyle: { backgroundColor: colors.background },
        }}
      >
        {needsOnboarding ? (
          <Stack.Screen
            name="Onboarding"
            component={OnboardingScreen}
            options={{ title: 'Onboarding - Wealfi' }}
          />
        ) : (
          <Stack.Screen
            name="Main"
            component={MainNavigator}
            options={{ title: 'Wealfi' }}
          />
        )}
        <Stack.Screen name="MagicAuth" component={MagicAuthScreen} />
        <Stack.Screen name="GoogleAuth" component={GoogleAuthScreen} />
        <Stack.Screen name="AuthError" component={AuthErrorScreen} />
      </Stack.Navigator>
    );
  }

  // Not authenticated - show auth navigator
  logger.log('[AppNavigator] Not authenticated - showing AuthNavigator');
  return (
    <Stack.Navigator
      screenOptions={{
        headerShown: false,
        cardStyle: { backgroundColor: colors.background },
      }}
    >
      <Stack.Screen name="Auth" component={AuthNavigator} />
      <Stack.Screen name="MagicAuth" component={MagicAuthScreen} />
      <Stack.Screen name="GoogleAuth" component={GoogleAuthScreen} />
      <Stack.Screen name="AuthError" component={AuthErrorScreen} />
    </Stack.Navigator>
  );
}
